import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  OpenChangeReason,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  UseFloatingOptions,
  useFocus,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react';
import { ReactNode, useRef, useState } from 'react';

type TooltipBaseProps = {
  options?: Partial<UseFloatingOptions>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  panelClassName?: string;
  interactive?: boolean;
};

type TooltipProps = TooltipBaseProps &
  (
    | { text: string; children?: ReactNode }
    | { text?: string; children: ReactNode }
  );

export default function Tooltip({
  text,
  options,
  children,
  open: openProp,
  onOpenChange: onOpenChangeProp,
  panelClassName,
  interactive = false
}: TooltipProps) {
  const [hoverOpen, setHoverOpen] = useState(false);
  const arrowRef = useRef(null);

  const isControlled = openProp !== undefined;
  const isOpen = (openProp ?? false) || hoverOpen;

  const setIsOpen = (
    next: boolean,
    _event?: Event,
    reason?: OpenChangeReason
  ) => {
    if (reason === 'outside-press' || reason === 'escape-key') {
      setHoverOpen(false);
      if (isControlled) onOpenChangeProp?.(false);
      return;
    }
    setHoverOpen(next);
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip(),
      shift({ padding: 5 }),
      arrow({
        element: arrowRef
      })
    ],
    ...options
  });

  const hover = useHover(context, {
    move: false,
    handleClose: interactive ? safePolygon() : null
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role
  ]);

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className='absolute inset-0'
      />
      <FloatingPortal>
        {isOpen && (
          <div
            className={
              panelClassName ??
              'z-50 w-max max-w-[300px] rounded bg-[#444] px-2 py-1 text-sm text-white'
            }
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {children ?? text}
            {panelClassName === undefined && (
              <FloatingArrow fill='#444' ref={arrowRef} context={context} />
            )}
          </div>
        )}
      </FloatingPortal>
    </>
  );
}
